import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Search, X } from 'lucide-react'

export interface DropdownOption {
  label: string
  value: string
}

function safeString(val: any): string {
  if (val === null || val === undefined) return ''
  if (typeof val === 'string') return val
  if (typeof val === 'number' || typeof val === 'boolean') return String(val)
  if (typeof val === 'object') {
    if (val.name) return safeString(val.name)
    if (val.label) return safeString(val.label)
    if (val.title) return safeString(val.title)
    if (val.value) return safeString(val.value)
    if (val.code) return safeString(val.code)
    if (val.id) return safeString(val.id)
    return JSON.stringify(val)
  }
  return String(val)
}

export type TeacherDropdownOption = DropdownOption

export interface DropdownProps {
  options: DropdownOption[]
  value: string
  onChange: (value: string) => void
  ariaLabel?: string
  className?: string
  buttonClassName?: string
  menuClassName?: string
  iconClassName?: string
  optionClassName?: string
  menuInnerClassName?: string
  variant?: 'default' | 'filled'
  error?: boolean
  disabled?: boolean
  placeholder?: string
  label?: string
  appearance?: 'outlined' | 'filled'
  square?: boolean
  menuAlign?: 'left' | 'right'
  leadingIcon?: React.ReactNode
  labelClassName?: string
  placement?: 'bottom' | 'top' | 'auto'
  searchable?: boolean
  placeholderAsValue?: boolean
}
 
// Teacher/Student dropdown — large, styled with teal theme
export function Dropdown({
  options,
  value,
  onChange,
  ariaLabel,
  className = '',
  buttonClassName = '',
  menuClassName = '',
  iconClassName = '',
  optionClassName = '',
  menuInnerClassName = '',
  variant = 'default',
  error = false,
  disabled = false,
  placeholder,
  label,
  appearance,
  leadingIcon,
  labelClassName = '',
  placement = 'bottom',
  searchable,
  placeholderAsValue = false,
}: DropdownProps) {
  const effectivePlaceholder = placeholder || label
  const effectiveVariant = appearance === 'filled' ? 'filled' : variant
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const rootRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; placeAbove?: boolean } | null>(null)

  const isSearchable = Boolean(searchable)

  const normalizedOptions = useMemo(() => {
    return (options || []).map((opt: any) => {
      if (!opt) return { label: '', value: '' }
      if (typeof opt === 'object') {
        const optLabel = safeString(opt.label || opt.name || opt.title || opt.value || opt.id || 'Option')
        const optVal = safeString(opt.value || opt.id || opt.name || opt.label || optLabel)
        return { ...opt, label: optLabel, value: optVal }
      }
      const str = safeString(opt)
      return { label: str, value: str }
    })
  }, [options])

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchTerm.trim()) return normalizedOptions
    const tokens = searchTerm.toLowerCase().trim().split(/\s+/).filter(Boolean)
    return normalizedOptions.filter((opt) => {
      const fieldValues: string[] = []
      const collectStrings = (target: any) => {
        if (!target || typeof target !== 'object') return
        for (const [key, val] of Object.entries(target)) {
          if (typeof val === 'string' || typeof val === 'number') {
            fieldValues.push(String(val))
          } else if ((key === 'raw' || key === 'staff') && typeof val === 'object' && val !== null) {
            collectStrings(val)
          }
        }
      }
      collectStrings(opt)
      const combinedText = fieldValues.join(' ').toLowerCase()
      return tokens.every((token) => combinedText.includes(token))
    })
  }, [normalizedOptions, searchTerm, isSearchable])

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setOpen(false)
        return
      }

      let placeAbove = false
      if (placement === 'top') {
        placeAbove = true
      } else if (placement === 'auto' || placement === 'bottom') {
        const spaceBelow = window.innerHeight - rect.bottom
        placeAbove = spaceBelow < 220 && rect.top > 150
      }

      const top = placeAbove ? 'auto' : `${rect.bottom + 4}px`
      const bottom = placeAbove ? `${window.innerHeight - rect.top + 4}px` : 'auto'
      const left = `${rect.left}px`
      const width = `${rect.width}px`

      if (menuRef.current) {
        menuRef.current.style.top = top
        menuRef.current.style.bottom = bottom
        menuRef.current.style.left = left
        menuRef.current.style.minWidth = width
      }

      setCoords({
        top: placeAbove ? rect.top : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        placeAbove,
      })
    }
  }

  useLayoutEffect(() => {
    if (open) {
      updateCoords()
    }
  }, [open])

  useEffect(() => {
    if (open) {
      updateCoords()
      setSearchTerm('')
      const handleScrollOrResize = () => updateCoords()
      window.addEventListener('resize', handleScrollOrResize)
      window.addEventListener('scroll', handleScrollOrResize, { capture: true, passive: true })
      return () => {
        window.removeEventListener('resize', handleScrollOrResize)
        window.removeEventListener('scroll', handleScrollOrResize, { capture: true })
      }
    }
  }, [open])

  useEffect(() => {
    if (open && isSearchable) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [open, isSearchable])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        !rootRef.current?.contains(target) &&
        !buttonRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [])

  const selectedOption = useMemo(() => {
    if (!value || typeof value !== 'string' || !value.trim()) return null
    const valStr = value.trim().toLowerCase()
    const found = normalizedOptions.find(
      (option) =>
        option.value === value ||
        option.label === value ||
        (option.value !== undefined && option.value !== null && String(option.value).toLowerCase() === valStr) ||
        (option.label !== undefined && option.label !== null && String(option.label).toLowerCase() === valStr)
    )
    if (found) return found

    // If value is a UUID and not matched in options, do NOT display raw UUID as label
    const isUuidVal = /^[0-9a-fA-F-]{36}$/.test(value.trim())
    if (isUuidVal) {
      return null
    }

    return { label: value, value }
  }, [normalizedOptions, value])

  const isPlaceholder = !value || !selectedOption
  const displayLabel = isPlaceholder && effectivePlaceholder ? effectivePlaceholder : selectedOption?.label || effectivePlaceholder || 'Select option'

  const defaultButtonStyles = error
    ? 'flex h-11 w-full items-center justify-between gap-2 rounded-[8px] border border-[#d92d20] bg-[#fffbfa] px-3.5 py-2.5 text-left text-[14px] font-sans font-normal text-[#101828] transition-all duration-150 focus:outline-none focus:ring-4 focus:ring-[#d92d20]/10 disabled:bg-[#f9fafb] disabled:text-[#667085] disabled:border-[#e4e7ec] disabled:cursor-not-allowed'
    : effectiveVariant === 'filled'
      ? 'flex h-11 w-full items-center justify-between gap-2 rounded-[8px] border border-[#cfdcdf] bg-white px-3.5 py-2.5 text-left text-[14px] font-sans font-normal text-[#101828] transition-all duration-150 hover:border-[#0b6166] focus:border-[#0b6166] focus:outline-none focus:ring-4 focus:ring-[#0b6166]/10 disabled:bg-[#f9fafb] disabled:text-[#667085] disabled:border-[#e4e7ec] disabled:cursor-not-allowed'
      : 'flex min-h-12 w-full items-center justify-between gap-4 rounded-[14px] border border-[#c7d3d4] bg-white pl-4 pr-5 py-3 text-left text-[15px] font-sans font-semibold text-[#132238] shadow-sm transition-all duration-200 hover:border-[#0b5f64] focus:outline-none disabled:bg-[#f9fafb] disabled:text-[#667085] disabled:border-[#e4e7ec] disabled:cursor-not-allowed'

  const errorButtonStyles = error
    ? '!border-[#d92d20] !bg-[#fffbfa] text-[#101828] focus:!border-[#d92d20] focus:!ring-4 focus:!ring-[#d92d20]/10'
    : ''

  return (
    <div ref={rootRef} className={`relative font-sans ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            updateCoords()
            setOpen((current) => !current)
          }
        }}
        className={`${
          buttonClassName
            ? `flex w-full items-center justify-between gap-2 border border-[#cfdcdf] transition-all duration-200 focus:outline-none disabled:bg-[#f9fafb] disabled:text-[#667085] disabled:border-[#e4e7ec] disabled:cursor-not-allowed ${buttonClassName}`
            : defaultButtonStyles
        } ${errorButtonStyles} ${
          open && !disabled
            ? effectiveVariant === 'filled'
              ? 'border-[#0b6166] ring-2 ring-[#0b6166]/15 bg-white'
              : 'border-[#0b5f64] ring-4 ring-[#0b5f64]/10 shadow-[5px_5px_0_rgba(11,95,100,0.18)]'
            : ''
        }`}
        aria-haspopup="listbox"
        aria-expanded={open && !disabled}
        aria-label={ariaLabel}
      >
        <div className={`flex items-center gap-2 min-w-0 flex-1 ${labelClassName?.includes('text-center') ? 'justify-center' : ''}`}>
          {leadingIcon ? <span className="shrink-0">{leadingIcon}</span> : null}
          <span
            title={displayLabel}
            className={`truncate font-sans ${
              labelClassName
                ? labelClassName
                : isPlaceholder
                  ? placeholderAsValue
                    ? 'text-left text-inherit font-medium'
                    : effectiveVariant === 'filled'
                      ? 'text-left text-slate-500 font-normal text-[13px]'
                      : 'text-left text-[#98a2b3] text-[13px]'
                  : 'text-left text-inherit font-medium'
            }`}
          >
            {displayLabel}
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 ${effectiveVariant === 'filled' ? 'text-slate-500' : 'text-[#0b5f64]'} transition-all duration-200 ${
            open && !disabled ? 'rotate-180' : ''
          } ${iconClassName}`}
        />
      </button>

      {open && !disabled && coords && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: coords.placeAbove ? 'auto' : `${coords.top}px`,
            bottom: coords.placeAbove ? `${window.innerHeight - coords.top + 4}px` : 'auto',
            left: `${coords.left}px`,
            minWidth: `${coords.width}px`,
            maxWidth: `calc(100vw - ${coords.left + 16}px)`,
          }}
          className={`z-[9999] overflow-hidden rounded-[10px] border border-[#cbd5e1] bg-white shadow-[0_20px_48px_rgba(15,23,42,0.25)] font-sans ${menuClassName}`}
          role="listbox"
        >
          {isSearchable ? (
            <div
              className="p-2 border-b border-[#e2e8f0] bg-[#f8fafc]"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 rounded-md border border-[#cbd5e1] bg-white px-2.5 py-1.5 focus-within:border-[#0b5f64] focus-within:ring-2 focus-within:ring-[#0b5f64]/15 transition-all">
                <Search className="h-3.5 w-3.5 text-[#64748b] shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation()
                    if (e.key === 'Escape') {
                      setOpen(false)
                    }
                  }}
                  placeholder="Search options..."
                  className="w-full bg-transparent text-[13px] text-[#0f172a] placeholder-[#94a3b8] outline-none font-sans font-normal"
                />
                {searchTerm ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSearchTerm('')
                      searchInputRef.current?.focus()
                    }}
                    className="text-[#94a3b8] hover:text-[#475569]"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className={`max-h-60 overflow-y-auto p-1.5 font-sans ${menuInnerClassName}`}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = option.value === value

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setOpen(false)
                      setSearchTerm('')
                      onChange(option.value)
                    }}
                    className={`flex w-full items-center justify-between gap-3 rounded-[8px] px-3.5 py-2.5 text-left text-sm font-sans font-medium transition-all duration-150 ${
                      isSelected
                        ? 'bg-[#E6F4F1] text-[#0b5f64] font-semibold shadow-xs'
                        : 'text-[#1e293b] hover:bg-[#f1f5f9] hover:text-[#0b5f64]'
                    } ${optionClassName}`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className="text-xs sm:text-sm font-medium text-slate-800 leading-snug">{option.label}</span>
                    {isSelected ? <Check className="h-4 w-4 shrink-0 text-[#0b5f64]" /> : null}
                  </button>
                )
              })
            ) : (
              <div className="px-4 py-3 text-center text-xs font-medium text-[#64748b] font-sans">
                No matching options
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
 
